import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { services, hubs } from '../../utils/assets/main';
import { cn } from '../../utils';
import { memo } from 'react';

interface CardProps {
  title: string;
  desc: string;
  img: string;
  index: number;
}

const InfoCard = memo(({ title, desc, img, index }: CardProps) => {
  const animation = {
    initial: { opacity: 0, y: 30 },
    whileInView: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 120,
        damping: 12,
        delay: index * 0.1,
      },
    },
  };

  return (
    <motion.div
      className='bg-[#FFFFFFB2] backdrop-blur-[15px] border border-[#FFFFFF] rounded-[10px] h-[135px] pt-5 pl-5 relative cursor-pointer'
      {...animation}
      viewport={{ once: true }}
      whileHover={{
        scale: 1.05,
        y: -5,
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      }}
      whileTap={{ scale: 0.98 }}
    >
      <motion.div
        className='text-[#222222] font-semibold text-[16px] mb-1 leading-[150%] tracking-[-0.025em]'
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        {title}
      </motion.div>
      <div className='pt-1'>
        {desc.split('\n').map((line, idx) => (
          <motion.p
            key={idx}
            className='text-[#555555] text-[14px] font-normal tracking-[-0.025em] tiny:text-[12px]'
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + idx * 0.1 }}
          >
            {line}
            <br />
          </motion.p>
        ))}
      </div>

      <div className='w-full mt-[5px] flex justify-end items-end pr-3 '>
        <motion.img
          src={img}
          alt={title}
          className='size-9'
          whileHover={{ scale: 1.2, rotate: 360 }}
          transition={{ type: 'spring', stiffness: 300 }}
        />
      </div>
    </motion.div>
  );
});

InfoCard.displayName = 'InfoCard';

interface SectionContentProps {
  title: string;
  description: string[];
  backgroundImage: string;
  items: typeof services | typeof hubs;
  link?: string;
}

const SectionContent = memo(
  ({
    title,
    description,
    backgroundImage,
    items,
    link,
  }: SectionContentProps) => {
    const animation = {
      initial: { opacity: 0, y: 50 },
      whileInView: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', stiffness: 100, damping: 15 },
      },
    };

    return (
      <motion.li
        className={cn('w-full text-white flex gap-6')}
        {...animation}
        viewport={{ once: true }}
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Link
          to={link || '#'}
          target={link ? '_blank' : undefined}
          className='w-full flex items-start p-5 relative rounded-[10px] overflow-hidden min-h-[320px] justify-between group'
        >
          <div className='relative z-10 flex flex-col justify-start items-start gap-[16px]'>
            <motion.div
              className='flex justify-start items-center text-[#1D3BD3] text-[24px] leading-[36px] font-[600]'
              whileHover={{ x: 5 }}
            >
              {title}
              {link && (
                <motion.img
                  src='/imgs/icons/circle-right-arrow.webp'
                  alt='우측 화살표'
                  className='h-[30px] object-contain ml-1'
                  whileHover={{ x: 5, scale: 1.1 }}
                />
              )}
            </motion.div>
            <div>
              {description.map((text, index) => (
                <motion.p
                  key={index}
                  className='text-[16px] leading-[150%] font-[400] text-[#000000] tracking-[-0.025em] text-wrap'
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  {text}
                </motion.p>
              ))}
            </div>
          </div>

          <div className='grid grid-cols-2 z-[1] gap-[10px] flex-1 max-w-[550px]'>
            {items.map((item, index) => (
              <InfoCard
                key={index}
                title={item.title}
                desc={item.desc}
                img={item.img}
                index={index}
              />
            ))}
          </div>
        </Link>
      </motion.li>
    );
  },
);

SectionContent.displayName = 'SectionContent';

const Section03 = memo(
  ({ contentsRef }: { contentsRef: React.RefObject<HTMLDivElement> }) => {
    const { scrollYProgress } = useScroll({
      target: contentsRef,
      offset: ['start end', 'end start'],
    });

    const opacity = useTransform(
      scrollYProgress,
      [0, 0.2, 0.8, 1],
      [0, 1, 1, 0],
    );
    const scale = useTransform(
      scrollYProgress,
      [0, 0.2, 0.8, 1],
      [0.8, 1, 1, 0.8],
    );
    const y = useTransform(
      scrollYProgress,
      [0, 0.2, 0.8, 1],
      [100, 0, 0, -100],
    );

    const headerAnimation = {
      initial: { opacity: 0, y: -30 },
      whileInView: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', stiffness: 100, damping: 15 },
      },
    };

    return (
      <motion.section
        className='section03 w-full relative'
        ref={contentsRef}
        style={{ opacity, y }}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div
          className='max-w-[1080px] pt-[100px] mx-auto relative'
          style={{ scale }}
        >
          <motion.h3
            className='text-[#2F46B8] font-[600] mb-[12px]'
            {...headerAnimation}
            viewport={{ once: true }}
          >
            협회 서비스
          </motion.h3>
          <motion.p
            className='text-[24px] leading-[38.4px] font-[600] tracking-[-0.025em]'
            {...headerAnimation}
            viewport={{ once: true }}
          >
            커뮤니티 활동과 결합된 영업의 가치를 높이는 프리미엄 인사이트를
            제공합니다.
          </motion.p>

          <div className='w-full flex flex-col justify-center items-center gap-3 mt-[40px]'>
            <SectionContent
              title='코딜톡 바로가기'
              description={[
                '소통과 정보 교류를 콘텐츠로 연결해',
                '가치를 높이고 함께 성장합니다.',
              ]}
              backgroundImage='/imgs/section03_li_bg_new.png'
              items={services}
              link='https://kodeal.fks.co.kr/talk'
            />

            <SectionContent
              title='코딜허브'
              description={[
                '가치를 확장하는 프리미엄 인사이트',
                '영업인의 성장과 혜택을 지속적으로 생산·제공합니다.',
              ]}
              backgroundImage='/imgs/section03_li_hub_bg.png'
              items={hubs}
            />
          </div>
        </motion.div>
      </motion.section>
    );
  },
);

Section03.displayName = 'Section03';

export default Section03;
